import React, { useState, useEffect } from "react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { uploadFilesToS3 } from "../services/files";
import { getEventsByType, getEventDates, getUploadSchema } from "../services/catalog";
import "../styles/Modal.css";

/**
 * Modos de carga por tipo de tarjeta:
 *   "full"       → evento + fecha (comportamiento por defecto)
 *   "event_only" → solo evento, sin fecha (INMUEBLES)
 *   "direct"     → sin dropdowns, se sube el archivo directo (CALENDARIO)
 *
 * Si el backend algún día manda `upload_mode` en el registro, ese gana.
 */
const UPLOAD_MODES = {
  INMUEBLES: "event_only",
  CALENDARIO: "direct"
};

const Modal = ({ isOpen, onClose, eventTypeData }) => {
  const typeKey = (eventTypeData?.event_type || "").trim().toUpperCase();

  const uploadMode =
    eventTypeData?.upload_mode || UPLOAD_MODES[typeKey] || "full";

  const needsEvent = uploadMode !== "direct";
  const needsDate = uploadMode === "full";

  // Estados para los datos de los Dropdowns
  const [eventsList, setEventsList] = useState([]);
  const [datesList, setDatesList] = useState([]);
  const [schema, setSchema] = useState(null);

  // Estados de la selección del usuario
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedDateObj, setSelectedDateObj] = useState(null);

  // Estados de carga y UI
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info"); // "info" | "success" | "error"

  const resetSelection = () => {
    setSelectedFiles([]);
    setUploadProgress(0);
    setCurrentFile("");
    setMessage("");
    setMessageType("info");
  };

  // 1. Al abrir el modal cargamos los eventos del tipo
  useEffect(() => {
    if (isOpen && eventTypeData) {
      setEventsList([]);
      setDatesList([]);
      setSelectedEvent("");
      setSelectedDateObj(null);
      setSchema(null);
      resetSelection();

      if (needsEvent) {
        setLoadingEvents(true);
        getEventsByType(eventTypeData.event_type)
          .then(data => setEventsList(data))
          .catch(() => {
            setMessage("Error cargando eventos");
            setMessageType("error");
          })
          .finally(() => setLoadingEvents(false));
      }

      if (eventTypeData.default_area) {
        getUploadSchema(eventTypeData.default_area)
          .then(config => setSchema(config))
          .catch(() => {
            console.warn("No se pudo cargar config de carga, usando defaults.");
          });
      }
    }
  }, [isOpen, eventTypeData, needsEvent]);

  // 2. Al elegir evento, cargamos fechas si el modo las pide
  const handleEventChange = async (e) => {
    const eventId = e.target.value;
    setSelectedEvent(eventId);

    setSelectedDateObj(null);
    setDatesList([]);
    resetSelection();

    if (!needsDate) return;

    if (eventId) {
      setLoadingDates(true);
      try {
        const dates = await getEventDates(eventId);
        setDatesList(dates);
      } catch (error) {
        setMessage("Error cargando fechas");
        setMessageType("error");
      } finally {
        setLoadingDates(false);
      }
    }
  };

  // 3. Selección de fecha
  const handleDateChange = (e) => {
    const dateId = e.target.value;
    setSelectedDateObj(datesList.find(d => d.event_date_id === dateId));
    resetSelection();
  };

  // 4. Selección de archivos
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setUploadProgress(0);
    setCurrentFile("");
    setMessage("");
    setMessageType("info");

    // Nombres repetidos dentro del mismo lote escriben la misma key en S3
    const names = files.map(f => f.name);
    const duplicated = names.filter((n, i) => names.indexOf(n) !== i);
    if (duplicated.length) {
      setMessage(`Hay nombres repetidos (${[...new Set(duplicated)].join(", ")}). Renómbralos antes de subir.`);
      setMessageType("error");
      setSelectedFiles([]);
      e.target.value = "";
      return;
    }

    setSelectedFiles(files);
  };

  const readyForFile =
    (!needsEvent || !!selectedEvent) &&
    (!needsDate || !!selectedDateObj);

  // 5. Validaciones y subida
  const handleFileUpload = async () => {
    if (selectedFiles.length === 0 || !readyForFile) {
      alert("Por favor completa todos los campos.");
      return;
    }

    // Validación de extensión (si el schema existe)
    if (schema?.allowed_extensions) {
      const invalid = selectedFiles.filter(file => {
        const ext = file.name.split(".").pop().toLowerCase();
        return !schema.allowed_extensions.includes(ext);
      });

      if (invalid.length) {
        alert(
          `Archivos con extensión no permitida: ${invalid.map(f => f.name).join(", ")}.\n` +
          `Permitidas: ${schema.allowed_extensions.join(", ")}`
        );
        return;
      }
    }

    setUploading(true);
    setMessage("Iniciando carga...");
    setMessageType("info");

    try {
      const eventObj = eventsList.find(ev => ev.event_id === selectedEvent);
      const displayName =
        eventObj?.display_name || selectedEvent || eventTypeData.label;

      const metadata = {
        // ⚠️ En modo "direct" no hay evento: la carpeta destino es el tipo.
        projectId: needsEvent ? selectedEvent : typeKey,
        eventName: displayName,
        artist: displayName,
        category: typeKey,
        ...(needsDate
          ? { date: selectedDateObj?.date, city: selectedDateObj?.city }
          : {}
        )
      };

      const results = await uploadFilesToS3(
        selectedFiles,
        metadata,
        (percent, { filename, index, total }) => {
          setCurrentFile(`${filename} (${index + 1}/${total})`);
          // Progreso global: archivos completos + avance del actual
          setUploadProgress(Math.round(((index + percent / 100) / total) * 100));
        }
      );

      const failed = results.filter(r => !r.ok);

      if (failed.length === 0) {
        setUploadProgress(100);
        setMessage(
          results.length === 1
            ? "¡Archivo cargado exitosamente!"
            : `¡${results.length} archivos cargados exitosamente!`
        );
        setMessageType("success");
        setTimeout(() => onClose(), 1500);
      } else {
        setMessage(
          `${results.length - failed.length} de ${results.length} archivos cargados. ` +
          `Fallaron: ${failed.map(f => f.filename).join(", ")}`
        );
        setMessageType("error");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setMessage("Error al subir los archivos.");
      setMessageType("error");
    } finally {
      setUploading(false);
      setCurrentFile("");
    }
  };

  if (!isOpen) return null;

  const fileLabel =
    selectedFiles.length === 0
      ? "Clic aquí para seleccionar archivos"
      : selectedFiles.length === 1
        ? selectedFiles[0].name
        : `${selectedFiles.length} archivos seleccionados`;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Cargar material: {eventTypeData.label}</h2>
          {schema?.help_text && <small className="help-text">{schema.help_text}</small>}
        </div>

        <div className="modal-body">
          {/* Dropdown 1: Evento/Artista */}
          {needsEvent && (
            <div className="form-group">
              <label>Selecciona Evento/Artista:</label>
              <select
                value={selectedEvent}
                onChange={handleEventChange}
                disabled={loadingEvents || uploading}
              >
                <option value="">-- Seleccionar --</option>
                {eventsList.map(ev => (
                  <option key={ev.event_id} value={ev.event_id}>
                    {ev.display_name}
                  </option>
                ))}
              </select>
              {loadingEvents && <span className="loader-text">Cargando...</span>}
            </div>
          )}

          {/* Dropdown 2: Fechas */}
          {needsDate && (
            <div className="form-group">
              <label>Selecciona Fecha:</label>
              <select
                value={selectedDateObj?.event_date_id || ""}
                onChange={handleDateChange}
                disabled={!selectedEvent || loadingDates || uploading}
              >
                <option value="">-- Seleccionar Fecha --</option>
                {datesList.map(d => (
                  <option key={d.event_date_id} value={d.event_date_id}>
                    {d.date} — {d.venue} ({d.city})
                  </option>
                ))}
              </select>
              {loadingDates && <span className="loader-text">Buscando fechas...</span>}
            </div>
          )}

          {/* Input de Archivos */}
          {readyForFile && (
            <div className="upload-area">
              <input
                type="file"
                id="file-input"
                multiple
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <label htmlFor="file-input" className="file-drop-zone">
                <FaCloudUploadAlt className="upload-icon-large" />
                <p>{fileLabel}</p>
              </label>

              {selectedFiles.length > 1 && (
                <ul className="selected-files">
                  {selectedFiles.map(file => (
                    <li key={file.name}>{file.name}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Barra de Progreso */}
          {uploading && (
            <>
              <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${uploadProgress}%` }}>
                  {uploadProgress}%
                </div>
              </div>
              {currentFile && <span className="loader-text">Subiendo {currentFile}</span>}
            </>
          )}

          {/* Mensaje */}
          {message && (
            <p className={`status-message ${messageType}`}>
              {message}
            </p>
          )}
        </div>

        <div className="modal-footer">
          <button className="button-cancel" onClick={onClose} disabled={uploading}>
            Cancelar
          </button>
          <button
            className="button-send"
            onClick={handleFileUpload}
            disabled={selectedFiles.length === 0 || uploading}
          >
            {uploading
              ? "Subiendo..."
              : selectedFiles.length > 1
                ? `Subir ${selectedFiles.length} archivos`
                : "Subir Archivo"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Modal;
